import { z } from 'zod'

import type { Database } from '~/shared/common/supabase/database.types'
import {
  VenueTrustSignalDetail as VenueTrustSignalDetailSchema,
  type VenueTrustSignalDetail,
} from '~/shared/venue/venue-trust-signal.api.schema'

type VenueTrustSignalRow = Database['public']['Tables']['main_venuetrustsignal']['Row']
type VenueTrustSignalDetailRow = Pick<
  VenueTrustSignalRow,
  | 'source_title'
  | 'source_last_updated_date'
  | 'source_published_date'
  | 'venue_description'
  | 'signal_url'
>

const VenueTrustSignalDetailRowFromSupabase = z.object({
  source_title: z.string().nullable(),
  source_last_updated_date: z.string().nullable(),
  source_published_date: z.string().nullable(),
  venue_description: z.string().nullable(),
  signal_url: z.string().nullable(),
}) satisfies z.ZodType<VenueTrustSignalDetailRow, VenueTrustSignalDetailRow>

const VenueTrustSignalDetailRowsFromSupabase = z.array(VenueTrustSignalDetailRowFromSupabase)

function hasSourceTitle(row: VenueTrustSignalDetailRow): row is VenueTrustSignalDetailRow & { source_title: string } {
  return typeof row.source_title === 'string' && row.source_title.trim().length > 0
}

function getNewestSignalTimestamp(row: VenueTrustSignalDetail): number {
  const lastUpdated = row.sourceLastUpdatedDate ? Date.parse(row.sourceLastUpdatedDate) : Number.NEGATIVE_INFINITY
  const published = row.sourcePublishedDate ? Date.parse(row.sourcePublishedDate) : Number.NEGATIVE_INFINITY

  return Math.max(
    Number.isFinite(lastUpdated) ? lastUpdated : Number.NEGATIVE_INFINITY,
    Number.isFinite(published) ? published : Number.NEGATIVE_INFINITY,
  )
}

export function parseVenueTrustSignalDetailsFromSupabase(rows: unknown): VenueTrustSignalDetail[] {
  return VenueTrustSignalDetailRowsFromSupabase.parse(rows ?? [])
    .filter(hasSourceTitle)
    .map((row) => VenueTrustSignalDetailSchema.parse({
      sourceTitle: row.source_title,
      sourceLastUpdatedDate: row.source_last_updated_date,
      sourcePublishedDate: row.source_published_date,
      venueDescription: row.venue_description,
      signalUrl: row.signal_url,
    }))
    .sort((a, b) => getNewestSignalTimestamp(b) - getNewestSignalTimestamp(a))
}
